/** Plain-English wording for programs, shared by the list and the editor. No React here. */
import type { Device, Program, Run } from "./api.ts";
import { portLabel } from "./format.ts";

export const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function timeText(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number) as [number, number];
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}

export function daysText(days: number[]): string {
  const d = [...days].sort((a, b) => a - b);
  if (d.length === 7) return "Every day";
  if (d.join() === "1,2,3,4,5") return "Weekdays";
  if (d.join() === "0,6") return "Weekends";
  return d.map((x) => DAY_NAMES[x]).join(", ");
}

export function minutesText(min: number): string {
  if (min < 59.95) return `${Math.round(min * 10) / 10} min`;
  const whole = Math.round(min);
  const h = Math.floor(whole / 60);
  const m = whole % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

type Named = (eui: string) => Device | undefined;
const devName = (dev: Named, eui: string) => dev(eui)?.name ?? "a device";
const outName = (dev: Named, eui: string, port: string) => {
  const d = dev(eui);
  return d ? portLabel(d, port) : port;
};

export function ruleText(dev: Named, c: { devEui: string; port: string; op: string; value?: number; value2?: number; forSec: number }, unit = ""): string {
  const what = `${outName(dev, c.devEui, c.port)} (${devName(dev, c.devEui)})`;
  const u = unit ? ` ${unit}` : "";
  const cond =
    c.op === "on" ? "is on" : c.op === "off" ? "is off" : c.op === ">" ? `goes above ${c.value}${u}` : c.op === "<" ? `goes below ${c.value}${u}` : `is between ${c.value} and ${c.value2}${u}`;
  return `${what} ${cond}${c.forSec ? ` for ${c.forSec >= 60 ? minutesText(c.forSec / 60) : `${c.forSec} s`}` : ""}`;
}

export function startText(dev: Named, p: Program): string {
  if (!p.start.length) return "Started by hand";
  return p.start
    .map((c) =>
      c.type === "schedule"
        ? `${daysText(c.days)} at ${timeText(c.time)}`
        : c.type === "once"
          ? new Date(c.at).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
          : `When ${ruleText(dev, c)}`,
    )
    .join(" · ");
}

export const STATE_TEXT: Record<Run["state"], { label: string; tone: string }> = {
  idle: { label: "Waiting", tone: "" },
  running: { label: "Running", tone: "good" },
  pausing: { label: "Pausing", tone: "warn" },
  paused: { label: "Paused", tone: "warn" },
  stopping: { label: "Stopping", tone: "warn" },
  finished: { label: "Finished", tone: "info" },
  stopped: { label: "Stopped", tone: "" },
  failed: { label: "Stopped: problem", tone: "crit" },
};

/** "Step 1 of 2: Pump run (Booster pump), 12 min left" while running. */
export function whereText(dev: Named, p: Program, r: Run, now: number): string {
  if (r.state === "paused") return `Paused at step ${(r.pausedStep ?? r.index) + 1} of ${p.steps.length}`;
  if (r.state !== "running" && r.state !== "pausing" && r.state !== "stopping") return "";
  if (r.phase === "start") return r.waiting ? "Start actions: waiting for the device to confirm" : "Start actions";
  if (r.phase === "stop") return r.waiting ? "Switching off: waiting for the device to confirm" : "Stop actions";
  const nameOf = (i: number) => {
    const s = p.steps[i]!;
    return `${outName(dev, s.devEui, s.port ?? `${s.kind}${s.channel}`)} (${devName(dev, s.devEui)})`;
  };
  // Steps run on their own times and can overlap: say each one that's on.
  const steps = r.steps ?? [];
  const on = steps.flatMap((st, i) => (st.state === "on" && st.onAt !== undefined && p.steps[i] ? [i] : []));
  if (on.length) {
    return on
      .map((i) => {
        const s = p.steps[i]!;
        const st = steps[i]!;
        const left = s.runMin - (st.doneMs + (now - st.onAt!)) / 60_000;
        return `Step ${i + 1} of ${p.steps.length}: ${nameOf(i)} on${s.untilStop ? " until stopped" : `, ${minutesText(Math.max(0, left))} left`}`;
      })
      .join(" · ");
  }
  const turning = steps.findIndex((st) => st.cmd?.on);
  if (turning >= 0 && p.steps[turning]) return `Step ${turning + 1} of ${p.steps.length}: turning on ${nameOf(turning)}`;
  const i = r.index;
  if (!p.steps[i]) return "";
  // Between steps: when the next one starts on the program's clock.
  const el = (r.clockMs ?? 0) + (r.clockFrom !== undefined ? now - r.clockFrom : 0);
  const inMin = (p.steps[i]!.waitSec * 1000 - el) / 60_000;
  return `Step ${i + 1} of ${p.steps.length}: ${nameOf(i)} starts ${inMin > 0.5 ? `in ${minutesText(inMin)}` : "now"}`;
}

export function totalMinutes(p: Pick<Program, "steps">): number {
  return p.steps.reduce((n, s) => n + s.runMin, 0);
}

/** "0:15" / "1:30:20": a time from when the steps begin, as a clock would show it. */
export function clockText(sec: number): string {
  const t = hms(sec);
  const mm = String(t.m).padStart(2, "0");
  return t.s ? `${t.h}:${mm}:${String(t.s).padStart(2, "0")}` : `${t.h}:${mm}`;
}

/** Seconds as hours, minutes and seconds, for the time boxes. */
export function hms(sec: number): { h: number; m: number; s: number } {
  const t = Math.max(0, Math.round(sec));
  return { h: Math.floor(t / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
}

/** A program part-way through a run (running, paused, or on its way to stopping or pausing). */
export function anyActive(views: ReadonlyArray<{ run: { state: Run["state"] } }> | undefined): boolean {
  return (views ?? []).some((v) => v.run.state !== "idle" && v.run.state !== "finished" && v.run.state !== "stopped" && v.run.state !== "failed");
}

/**
 * How often a screen asks for /programs: `fast` while a program is mid-run (its progress bar
 * should move), every 30 s otherwise; a waiting program changes only when someone edits it
 * or it starts, and a start shows within the 30 s.
 */
export const programsPollMs = (views: Parameters<typeof anyActive>[0], fast: number) => (anyActive(views) ? fast : 30_000);
